import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import type MathsFluencySprintsStudentRoute from 'teacher-ui-client/reporting/ui-scope/maths/fluency-sprints/student/route'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { StatBoxesFluencySprints } from 'teacher-ui-client/components/stat-boxes/fluency-sprints'
import { StarLegend } from 'teacher-ui-client/components/tables/fluency-sprints/star-legend'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { t, type IntlService } from 'ember-intl'
import { hash } from '@ember/helper'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { array } from '@ember/helper'
import { isPresent } from '@ember/utils'

interface Signature {
  Args: {
    model: ModelFor<MathsFluencySprintsStudentRoute>
  }
}
export class MathsFluencySprintsStudentPage extends Component<Signature> {
  @service intl!: IntlService

  get title() {
    const studentName = this.args.model.student.fullName
    return this.intl.t('reporting.title.studentDetails.fluencySprints', { studentName })
  }

  get noData() {
    return !isPresent(this.args.model.mentalMinuteSummary)
  }

  get previousRouteName() {
    return this.args.model.previousRouteName
  }

  <template>
    <div>
      <ReportingTeacherUiHeader
        @title={{this.title}}
        @returnLinkText={{t "reporting.maths.fluencySprints.student.returnLink"}}
        @returnLinkRoute={{this.previousRouteName}}
      />

      <ReportingStudentSummaryControls
        @students={{@model.students}}
        @student={{@model.student}}
        @exportOptions={{array ExportType.CSV ExportType.print}}
        @isDataPresent={{Boolean @model.mentalMinuteSummary}}
        @studentRouteName="reporting.ui-scope.maths.fluency-sprints.student.table"
        @allStudentsRouteName={{this.previousRouteName}}
      />

      <ReportingNoDataMessageIf @on={{this.noData}}>

        <div class="page-break-after-always rounded-md p-3 shadow-md print:shadow-none">
          <div class="text-right print:hidden">
            <span>
              <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
              <Tooltip @text={{t "reporting.maths.fluencySprints.statBoxes.tooltip.student"}} />
            </span>
          </div>
          {{#if @model.mentalMinuteSummary}}
            <StatBoxesFluencySprints @data={{@model.mentalMinuteSummary}} @dataScope="student" />
          {{/if}}
        </div>

        <div class="mt-6">
          <StarLegend @showNotPassed={{false}} class="text-right" />

          <TabGroupComponent @tabType={{TAB_TYPE_KEYS.shadow}} as |tabGroup|>
            <tabGroup.linkToTab
              @theme="green"
              @route="reporting.ui-scope.maths.fluency-sprints.student.table"
              @models={{array @model.student.id}}
              @query={{hash category="all"}}
              data-test-student-tab-link="all"
            >
              {{t "reporting.maths.fluencySprints.student.tables.titles.all"}}
            </tabGroup.linkToTab>

            <tabGroup.linkToTab
              @theme="blue"
              @route="reporting.ui-scope.maths.fluency-sprints.student.table"
              @models={{array @model.student.id}}
              @query={{hash category="addition"}}
              data-test-student-tab-link="addition"
            >
              {{t "reporting.maths.fluencySprints.student.tables.titles.addition"}}
            </tabGroup.linkToTab>

            <tabGroup.linkToTab
              @theme="orange"
              @route="reporting.ui-scope.maths.fluency-sprints.student.table"
              @models={{array @model.student.id}}
              @query={{hash category="subtraction"}}
              data-test-student-tab-link="subtraction"
            >
              {{t "reporting.maths.fluencySprints.student.tables.titles.subtraction"}}
            </tabGroup.linkToTab>
            <tabGroup.linkToTab
              @theme="green"
              @route="reporting.ui-scope.maths.fluency-sprints.student.table"
              @models={{array @model.student.id}}
              @query={{hash category="multiplication"}}
              data-test-student-tab-link="multiplication"
            >
              {{t "reporting.maths.fluencySprints.student.tables.titles.multiplication"}}
            </tabGroup.linkToTab>
            <tabGroup.linkToTab
              @theme="purple"
              @route="reporting.ui-scope.maths.fluency-sprints.student.table"
              @models={{array @model.student.id}}
              @query={{hash category="division"}}
              data-test-student-tab-link="division"
            >
              {{t "reporting.maths.fluencySprints.student.tables.titles.division"}}
            </tabGroup.linkToTab>
          </TabGroupComponent>

          <ContentTile class="relative overflow-x-auto rounded-tl-none print:border-none">
            {{outlet}}
          </ContentTile>
        </div>

      </ReportingNoDataMessageIf>
    </div>
  </template>
}

export default RouteTemplate(MathsFluencySprintsStudentPage)
